import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { Map24Regular } from '@fluentui/react-icons';
import {
  PLAN_CAMERA_LEGEND,
  PlanEntityLegendEntry,
  PlanLegend,
  TwinPlanner,
} from '@repo/ui/planner';
import type {
  ActiveStreamDrag,
  PlanEntityMarks,
  StreamClaimCheck,
  StreamClaimResult,
} from '@repo/ui/planner';
import {
  CameraWall,
  cameraWallShownFeeds,
  compareCameraWallFeeds,
  useCameraWallFeeds,
  type CameraWallFeed,
  type CameraWallFeedDrag,
  type CameraWallPlacement,
  type CameraWallPlacementFilter,
  type CameraWallPlacementProp,
  type CameraWallSort,
  type CameraWallState,
} from '@repo/ui';
import {
  Banner,
  Button,
  DockedCard,
  DockedCardFloating,
  Icon,
  ListPanel,
  Skeleton,
  DOCKED_CARD_WIDTHS,
} from '@repo/design-system';
import { semantic, space, typeRole, weight, zIndex } from '@repo/design-tokens';
import {
  flyToOnPlan,
  useCameraCatalogStore,
  useEditorStore,
  useFloorplanStore,
} from '@repo/front-end-state';
import {
  asArray,
  effectiveCoverage,
  findLatestDesign,
  floorCameraCounts,
  isLive,
  layoutCurrentDesigns,
  cameraDisplayName,
} from '@repo/front-end-utils';
import { checkSkeletonDetections } from '@repo/front-end-services';
import type { SkeletonDetectionCheckOutcome } from '@repo/front-end-services';
import type { FloorPlannerCamera, FloorPlannerLayout, RTSPEndpoint } from '@repo/frontend-types';

import Config from '../../common/Config';
import CameraPanel, { type CameraPanelStreamsStatus } from './CameraPanel';
import CameraCalibration from './CameraCalibration';
import { coverageReadout, floorLabel } from './cameraPanelText';
import { getSeenByNoCamera, type SeenByNoCamera } from './cameraSight';
import LinkReplaceModal from './LinkReplaceModal';
import { streamDisplayName } from './streamDisplayName';
import { nameAfterLink, nameAfterUnlink, replaceSentences } from './streamLinkRules';
import { hydrateObserverSession } from './observerSessionState';
import { useCameraPanelHost } from './useCameraPanelHost';
import { PlanCard, usePlanCardLayer } from './planCard';
import {
  BAR_BOTTOM,
  GLASS_EDGE,
  GLASS_EDGE_PX,
  GLASS_GAP,
  besideCardRight,
  GLASS_START,
  NARROW_VIEWPORT_QUERY,
  PANEL_TOP,
  PANEL_TOP_PX,
  WALL_WIDTH,
  WALL_WIDTH_NARROW,
  useMatchesMedia,
} from './glassLayout';
import { usePlanCoverInsets } from './planCoverInsets';
import { useZoomRegionsByCamera } from './useZoomRegionsByCamera';
import type { LiveTwinConfiguration } from './useLiveTwinConfiguration';

/**
 * Recency window for the "is this feed producing detections?" gate. Recent
 * enough to mean "live now", wide enough to tolerate setup gaps — and it bounds
 * the middleware query so it doesn't scan/sort the twin's whole detection
 * history (which trips the Postgres statement timeout on busy twins).
 */
const SKELETON_CHECK_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * The injected stream list while the page's read has not produced one.
 *
 * A module constant rather than an inline `[]` so its identity is stable across renders — the feed
 * hook keys its seed effect on the stream ids rather than on array identity, so this is belt and
 * braces, but a fresh array per render on a 30-tile wall is not a thing to leave to a downstream
 * implementation detail.
 */
const NO_STREAMS: RTSPEndpoint[] = [];

/** The plan header's camera legend (`camera-feeds-page-stage`, R6–R7): one element, never rebuilt. */
const CAMERA_LEGEND = <PlanLegend direction="row" entries={PLAN_CAMERA_LEGEND} />;
/**
 * The same legend while something on the floor is seen by no camera (`onboarding-status-colours-unseen`,
 * R6): the shared camera entries, then the problem dot. Composed here rather than added to
 * `PLAN_CAMERA_LEGEND`, which the Observers stage also shows. One element too, so the strip's slot
 * changes only when the entry comes or goes.
 */
const CAMERA_LEGEND_WITH_UNSEEN = (
  <PlanLegend
    direction="row"
    entries={[
      ...PLAN_CAMERA_LEGEND,
      { key: 'unseen', node: <PlanEntityLegendEntry look="check" label="Seen by no camera" /> },
    ]}
  />
);

/** The camera card's home: below the nav bar, at the 8 px edge (R6.1). */
const CARD_HOME_INSET = {
  top: PANEL_TOP_PX,
  right: GLASS_EDGE_PX,
  bottom: GLASS_EDGE_PX,
  left: GLASS_EDGE_PX,
};
/** The room the card layer leaves a tall card: the same top and bottom clearance. */
const CARD_ROOM = { top: PANEL_TOP_PX, bottom: GLASS_EDGE_PX };
/** How long typing must rest before the search says how many streams it shows (R3.4). */
const SEARCH_ANNOUNCE_MS = 500;

const countOf = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/**
 * What the step's unseen region says (`onboarding-status-colours-unseen`, R7): nothing on a floor with
 * no camera, the counts while anything is unseen, and that everything is seen otherwise.
 */
const unseenSentence = (unseen: (SeenByNoCamera & { hasCamera: boolean }) | null): string => {
  if (!unseen?.hasCamera) return '';
  const { size: spaces } = unseen.spaces;
  const { size: machines } = unseen.machines;
  if (spaces === 0 && machines === 0) {
    return 'Every space and machine on this floor is seen by a camera.';
  }
  return `Seen by no camera: ${countOf(spaces, 'space')}, ${countOf(machines, 'machine')}.`;
};

/**
 * The planner's marks for a set of unseen ids, spelled as JSON (`onboarding-status-colours-unseen`,
 * Technical Approach): built from the ids' content rather than from the result object, so a camera
 * edit that leaves the same spaces and machines unseen hands the planner the same marks.
 */
const unseenMarksOf = (key: string): PlanEntityMarks | undefined => {
  const [spaces, machines] = JSON.parse(key) as [string[], string[]];
  if (spaces.length === 0 && machines.length === 0) return undefined;
  return {
    spaces: new Map(spaces.map(id => [id, 'check' as const])),
    machines: new Map(machines.map(id => [id, 'check' as const])),
  };
};

/*
 * What the detection check found — a NOTICE, never a gate.
 *
 * This used to be a three-value `connectivity` flag whose non-`connected` states replaced the
 * entire stage with "Not connected — check the edge device is online and its cameras are
 * streaming". Nothing on this stage reads detections: placing a camera, naming it and linking
 * it to a stream all work with the box unplugged. So the check blocked work it had no bearing
 * on, and — because the client collapsed every failure into a negative answer — it did so while
 * making a false claim about the customer's hardware.
 *
 * One state per thing we can actually establish, so no state can be reached without a sentence
 * written for it. `detections` renders nothing at all: the happy path is silence.
 */
type CheckState =
  | { kind: 'checking' }
  /** No `?twinId=` — there is nothing to check against, which is not the same as a failure. */
  | { kind: 'not-checked' }
  | { kind: 'detections' }
  /** Detections found, but at least one of the twin's devices could not be read. */
  | { kind: 'detections-partial'; deviceCount: number | null }
  /** The twin has no edge devices at all. Not "the device is offline" — there isn't one. */
  | { kind: 'no-devices' }
  /** Devices exist and produced nothing in the window. An answer, and a negative one. */
  | { kind: 'no-detections' }
  /** We could not find out. Never rendered as either of the two above. */
  | { kind: 'unavailable'; message: string }
  /**
   * No access token, so no request was ever issued. SEPARATE from `unavailable` because a retry
   * cannot help: `runCheck` returns before it does anything when there is no token, and the token
   * effect does not re-run — so offering a Check again button here renders a control that is
   * enabled, looks live, and provably does nothing (measured: two presses, zero calls, no state
   * change). A dead affordance is worse than none.
   */
  | { kind: 'no-session' };

/** Map the client's outcome onto what we can honestly say about it. */
const readOutcome = (outcome: SkeletonDetectionCheckOutcome): CheckState => {
  if (outcome.status !== 'checked') {
    // `unavailable` and `unreachable` differ only in how specific the sentence can be. Neither
    // is a negative answer, and neither may borrow one's wording.
    return { kind: 'unavailable', message: outcome.message };
  }
  const { received, deviceCount, partial } = outcome.result;
  // `typeof === 'number'`, NOT falsiness. A middleware deployed before this contract omits the
  // key, and an absent key is `undefined` — reading that as 0 would tell the operator this twin
  // has no edge device on the strength of an old server.
  const devices = typeof deviceCount === 'number' ? deviceCount : null;
  if (received) {
    return partial === true
      ? { kind: 'detections-partial', deviceCount: devices }
      : { kind: 'detections' };
  }
  return devices === 0 ? { kind: 'no-devices' } : { kind: 'no-detections' };
};

/** Compile-time exhaustiveness. A missed `CheckState` member becomes a type error, not a blank. */
const assertNever = (state: never): never => {
  throw new Error(`Unhandled check state: ${JSON.stringify(state)}`);
};

/*
 * BOTH AT MODULE SCOPE, and not for the allocation. `readOutcome` closes over nothing — no props,
 * no state, no hooks — so declaring it in the component body made it a fresh reference every
 * render that `runCheck`'s `useCallback` closed over without listing, and put the feature's most
 * delicate rule (an ABSENT `deviceCount` is unknown, never zero) somewhere only a full mocked
 * render could reach. Out here it is a stable reference and a plain unit-testable function.
 */

/**
 * Which floor holds a given camera, by name — for the refusal sentence when the holder is not on
 * the floor the operator is looking at. `null` when it cannot be found, in which case the caller
 * says "another floor" rather than naming one it is not sure about.
 */
const floorNameHolding = (
  layout: FloorPlannerLayout | null | undefined,
  refid: string,
): string | null => {
  for (const floor of layout?.floors ?? []) {
    const design = findLatestDesign(floor.designs);
    if (asArray(design?.cameras).some(c => c.refid === refid)) return floor.name ?? null;
  }
  return null;
};

interface Props {
  className?: string;
  /** Onboarding session whose floor plan (from the previous section) is annotated. */
  sessionId: string;
  /**
   * What the twin is running right now, read ONCE on the page and shared with the Observers stage.
   *
   * This stage reads only `streams` from it — the twin's RTSP endpoints, with `loading`, `ready`
   * and `failed` kept apart, and a `retry` that re-runs that read alone. Optional so a host that
   * has not loaded it yet still renders; absent, the stage says the streams have not been read
   * rather than claiming there are none.
   */
  liveConfiguration?: LiveTwinConfiguration;
  /**
   * Invoked after this stage writes a camera into the plan — a link, a placement, an unlink, a
   * field, the name once its field is left — and, through `TwinPlanner`'s `onCameraEdited`, after
   * the plan's own camera gestures (move, turn, the camera tool, delete). The wizard asks its draft
   * saver for a save (`camera-feeds-page-draft`).
   */
  onCameraEdit?: () => void;
  /**
   * The planner's report that a deletion took camera regions (`onboarding-polish-delete-cascade`).
   * The wizard passes its observer-edit handler, so the deletion stamps the observer draft and a
   * launched twin does not reload the running configuration's regions over it on the next open.
   */
  onObserversRemoved?: () => void;
}

/**
 * Step 3: point the twin's camera streams at the plan.
 *
 * The stage lists **every stream the twin has**, on the shared camera wall, whether or not anybody
 * has drawn a camera on the floor plan — which is the change this file exists for. It used to list
 * the plan's cameras instead, so on the two staging twins that have a saved plan (both holding
 * zero cameras against 14 and 8 live streams) it rendered one sentence telling the operator to go
 * back a step, and on production's six saved plans, between zero and two cameras against 8-46
 * streams.
 *
 * A stream is dragged from its tile to where its camera hangs, or linked to a camera already on the
 * plan from that camera's panel (its stream picker, Change and Unlink); the panel names it and sets
 * its coverage. Cameras are the only thing on the plan this stage may change.
 */
/** The refusal for a stream id this twin does not hold — the drop's and the drag hint's one sentence. */
const UNKNOWN_STREAM = 'That stream is not one this twin holds.';

/**
 * What claiming a stream for a camera would do (`checkStreamClaim`): refused with a sentence; free,
 * releasing a same-floor holder or nothing; or — the camera shows ANOTHER stream — a replace the
 * operator is asked about first (`camera-feeds-page-link`).
 */
type StreamClaimAnswer =
  | { refusal: string }
  | { release: FloorPlannerCamera | null }
  | { replace: { from: number; target: FloorPlannerCamera }; release: FloorPlannerCamera | null };

/**
 * A camera read from the STORE, not from the step's memoised list: a camera minted or written this
 * turn (a drop's, or the release `claimStream` has just written) is not in that list yet. Its index
 * is floor-local, as every name on the step is.
 */
const readLiveCamera = (refid: string) => {
  const { layout, activeFloorIndex } = useFloorplanStore.getState();
  const live = asArray(
    findLatestDesign(asArray(layout?.floors[activeFloorIndex]?.designs))?.cameras,
  ).filter(isLive);
  const index = live.findIndex(c => c.refid === refid);
  return { camera: index >= 0 ? live[index] : null, index, activeFloorIndex };
};

/**
 * Put focus on a camera's marker, or on the plan when the marker is gone — where focus belongs after
 * a confirm a drop opened, since a drop has no focused opener to go back to. A frame later, so it
 * lands after the modal has handed focus back to whatever was focused when it opened.
 */
const focusCameraOrPlan = (refid: string) =>
  window.requestAnimationFrame(() => {
    const marker = document.querySelector<HTMLElement>(
      `[data-camera-refid="${CSS.escape(refid)}"]`,
    );
    (
      marker ?? document.querySelector<HTMLElement>('[role="region"][aria-label="Floor plan"]')
    )?.focus();
  });

/**
 * A Replace the step is asking about: a stream dropped on a camera that shows another. The sentences
 * are fixed when the drop lands, so the confirm says what the operator saw; Replace re-checks the
 * plan before it writes.
 */
interface PendingReplace {
  refid: string;
  endpointId: number;
  from: number;
  /** The same-floor camera the confirm said would let go of the dropped stream, if any. */
  releaseRefid: string | null;
  title: string;
  body: string;
  row: string;
  oldName: string;
}

const OnboardingCameras: React.FC<Props> = ({
  className,
  sessionId,
  liveConfiguration,
  onCameraEdit,
  onObserversRemoved,
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [accessToken, setAccessToken] = useState<string>();
  /**
   * Whether the silent auth REFUSED, as opposed to not having answered yet.
   *
   * The wall cannot seed without a token, so "no feeds yet" means one of two opposite things and
   * the stage must not render them alike: still coming, or never coming. Without this the wait
   * below has no exit — the same defect, next door, that the `catch` further down was written for.
   */
  const [tokenRefused, setTokenRefused] = useState(false);
  const [searchParams] = useSearchParams();

  const layout = useFloorplanStore(s => s.layout);
  const storeTwinBID = useFloorplanStore(s => s.twinBID);
  const activeFloorIndex = useFloorplanStore(s => s.activeFloorIndex);
  const updateCamera = useFloorplanStore(s => s.updateCamera);
  const markEntityModified = useEditorStore(s => s.markEntityModified);

  // The floor plan is made on its own section — this one must never seed a
  // layout itself (doing so used to skip the Floor Plan chooser).
  const hasPlan = layout != null && storeTwinBID === sessionId;

  /*
   * Each camera's observer regions, for its hover zoom (`camera-zoom-dwell-and-observers`, FR6): the
   * PLAN's saved observers, the ones Go Live files. Read through `hasPlan` like everything else here:
   * the store's layout can still be the previous twin's, and although the stage then renders its
   * no-plan screen rather than the wall, this memo runs first (hooks precede that return) and has no
   * business hydrating another twin's plan. Recomputed when the plan changes, never per frame, and
   * `useZoomRegionsByCamera` hands an unchanged camera its previous array, so the wall's memo still
   * bails for every tile a plan edit did not touch.
   */
  const planLayout = hasPlan ? layout : null;
  const planObservers = useMemo(() => hydrateObserverSession(planLayout), [planLayout]);
  const zoomRegions = useZoomRegionsByCamera(
    planObservers.polygonsByEntity,
    planObservers.observerState,
  );
  const zoomRegionsFor = useCallback(
    (feed: CameraWallFeed) => zoomRegions.get(Number(feed.id)),
    [zoomRegions],
  );

  /** The plan's own pane, holding the plan card's layer over the canvas. */
  const planPaneRef = useRef<HTMLDivElement>(null);

  // Twin context comes from the Setup entry URL.
  const twinIdParam = Number(searchParams.get('twinId'));
  // `Number.isInteger`, matching `resolveRegisterScope`, the middleware's `parseTwinId` and the
  // admin planner. These three parses disagreed until `wizard-register-on-store`: `?twinId=1.5`
  // passed the wizard's own guard on `Number.isFinite`, rendered the whole wizard, and then could
  // not load a register — reporting zero assets on the Equipment tick while the planner sidebar
  // correctly refused to read anything.
  const twinId = Number.isInteger(twinIdParam) && twinIdParam > 0 ? twinIdParam : undefined;
  /*
   * Start in `select`. The camera tool (`C`, or the toolbar) still places cameras here — by a click,
   * or Enter at the view's centre — so this only clears a mode left behind by another stage.
   */
  useEffect(() => {
    useEditorStore.getState().setToolMode('select');
  }, []);

  const [check, setCheck] = useState<CheckState>({ kind: 'checking' });

  /*
   * Which check is the current one. Every issue takes a ticket; only the holder of the latest
   * ticket may write the answer.
   *
   * This is STRUCTURAL rather than defensive. Two concurrent checks are not reachable today only
   * because the retry button happens to be unmounted while `check.kind === 'checking'` — and the
   * accessibility fix below deliberately keeps that button mounted so focus survives a press,
   * which would make the race live. A stale `no-detections` landing on top of a fresh
   * `detections` is precisely the false claim this whole feature exists to remove, so it must not
   * rest on a rendering accident. The two effects below already use the same discipline.
   */
  const checkTicket = useRef(0);

  const runCheck = useCallback(async () => {
    if (!accessToken || !twinId) return;
    const ticket = ++checkTicket.current;
    setCheck({ kind: 'checking' });
    // Only look at recent detections: the question is "is the feed producing data *now*", not
    // "ever". The bound also keeps the query fast — without it the middleware sorts the twin's
    // entire detection history by event_time_ms and trips the Postgres statement timeout (57014)
    // on busy twins.
    const outcome = await checkSkeletonDetections(Config.middlewareApi, accessToken, twinId, {
      sinceMs: Date.now() - SKELETON_CHECK_WINDOW_MS,
    });
    // A superseded check says nothing. Dropping it is the whole point of the ticket.
    if (ticket !== checkTicket.current) return;
    setCheck(readOutcome(outcome));
  }, [accessToken, twinId]);

  useEffect(() => {
    if (!twinId) {
      /*
       * TAKE A TICKET HERE TOO, even though nothing is being issued. Advancing it is what
       * INVALIDATES a check already in flight: without this line an answer for the twin that was
       * just deselected still holds the current ticket, lands after this state is set, and
       * overwrites "no twin is selected" with a result for a twin that is no longer selected.
       * The guard below tests `!==`, not "is there a newer request", so the counter has to move.
       */
      checkTicket.current += 1;
      // No twin context ⇒ nothing to check against. Distinct from a failed check, and worded so.
      setCheck({ kind: 'not-checked' });
      return;
    }
    void runCheck();
  }, [twinId, runCheck]);

  // Resolve the access token once (camera catalogue + feed thumbnails).
  useEffect(() => {
    let cancelled = false;
    void getAccessTokenSilently()
      .then(token => {
        if (cancelled) return;
        setAccessToken(token);
        void useCameraCatalogStore.getState().fetchCameras(Config.middlewareApi, token);
      })
      .catch(error => {
        if (cancelled) return;
        /*
         * WITHOUT THIS THE LOADING STATE HAS NO EXIT. `runCheck` returns early when there is no
         * token, without touching state, so a rejected silent auth — blocked third-party cookies,
         * a lapsed refresh token, an Auth0 blip — left the banner reading "Checking for camera
         * activity…" for ever, with no retry affordance and only an unhandled rejection in the
         * console. The axios timeout cannot save it: no request is ever issued.
         */
        console.error('Skeleton-detection check: could not obtain an access token:', error);
        setCheck({ kind: 'no-session' });
        setTokenRefused(true);
      });
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  const floor = layout?.floors[activeFloorIndex];
  const design = floor ? findLatestDesign(floor.designs) : null;

  /*
   * The ACTIVE FLOOR's cameras — what the operator can pick, place and see on the canvas.
   *
   * `useMemo` on the design rather than a bare `.filter()`: this array is the dependency of the
   * endpoint index, the placement effect and three callbacks below, so a fresh array per render
   * made every one of them recompute on every render — including on each landed camera frame.
   */
  const cameras: FloorPlannerCamera[] = useMemo(
    () => (design?.cameras ?? []).filter(c => !c._deleted),
    [design],
  );

  /*
   * What no camera on the active floor sees (`onboarding-status-colours-unseen`, R1–R5): spaces and
   * machines wear the problem look, the legend names it and a polite region counts it.
   *
   * From a DEFERRED design, so a camera edit lands on the plan first and the sight union follows; the
   * cameras come from that same design, never from `cameras` above, so the marks, the legend and the
   * sentence always describe one design and an overtaken result is never shown once the latest has
   * settled. Gated on `hasPlan` like `planLayout`: the store can still hold the previous twin's plan.
   */
  const sightDesign = useDeferredValue(hasPlan ? design : null);
  const unseen = useMemo(() => {
    if (!sightDesign) return null;
    const sightCameras = sightDesign.cameras ?? [];
    return {
      ...getSeenByNoCamera(sightDesign, sightCameras),
      hasCamera: sightCameras.some(c => !c._deleted),
    };
  }, [sightDesign]);
  const hasUnseen = unseen != null && (unseen.spaces.size > 0 || unseen.machines.size > 0);
  const unseenKey = JSON.stringify([
    unseen ? Array.from(unseen.spaces) : [],
    unseen ? Array.from(unseen.machines) : [],
  ]);
  const unseenMarks = useMemo(() => unseenMarksOf(unseenKey), [unseenKey]);
  const unseenAnnouncement = unseenSentence(unseen);

  /*
   * EVERY floor's cameras — and the distinction is not tidiness, it is a correctness boundary.
   *
   * A stream belongs to the TWIN; a camera sits on ONE FLOOR. So every question of the form "does
   * a camera already hold this stream?" has to be asked of the whole plan, or a stream held by a
   * camera downstairs reads as unheld upstairs — the picker offers it, the claim finds no holder,
   * the cross-floor refusal does not fire, and the plan is saved with two cameras claiming one
   * stream. That is the invariant the refusal exists to enforce, defeated by asking the wrong set.
   *
   * The page derives the same all-floor list for its own counts (`layoutCurrentDesigns`).
   */
  const camerasAllFloors: FloorPlannerCamera[] = useMemo(
    () =>
      layoutCurrentDesigns(layout)
        .flatMap(d => asArray(d?.cameras))
        .filter(isLive),
    [layout],
  );

  /* ---------------- the wall ---------------- */

  const streamsRead = liveConfiguration?.streams;

  /*
   * The wall is fed the page's ALREADY-READ streams, so this stage issues no listing request of
   * its own.
   *
   * `undefined` — not `[]` — while the read is loading or has failed: an empty array is a claim
   * that the twin has no streams, and the wall renders `EmptyInline` ("This twin has no RTSP
   * streams.") when it is given one. That sentence must be reachable ONLY from a read that
   * succeeded, which is why the failed branch below renders no wall at all.
   */
  const streams = streamsRead?.status === 'ready' ? streamsRead.value : undefined;

  // Sort and filter are lifted to the host: `CameraWall` is a pure function of its props.
  const [wallSort, setWallSort] = useState<CameraWallSort>({ key: 'name', dir: 1 });
  const [wallFilters, setWallFilters] = useState<CameraWallState[]>([]);
  /*
   * The wall's Not placed / Placed / All switch (`camera-feeds-page-wall`). All by default — the
   * wall then groups Not placed first — and not persisted. `wallFilters` keeps its type: on a
   * placement wall the wall only ever sets it to `[]` (All) or a single state.
   */
  const [placementFilter, setPlacementFilter] = useState<CameraWallPlacementFilter>('all');

  const { feeds, isRefreshing, refreshAll, refreshFeed } = useCameraWallFeeds({
    baseUrl: Config.middlewareApi,
    accessToken,
    twinId: twinId ?? null,
    /*
     * `?? NO_STREAMS`, and the fallback is the whole point rather than tidiness.
     *
     * `endpoints` is what tells the hook the host owns the listing. Passing `undefined` on the
     * renders before the page's read settles — or after it fails — hands the listing straight back
     * to the hook, which then issues exactly the second read this injection exists to prevent, and
     * tears the wall down again when the real list arrives and the key changes. An empty array is
     * the honest statement while a read the HOST owns has not produced anything yet.
     */
    endpoints: streams ?? NO_STREAMS,
  });

  /**
   * Which plan camera, if any, holds a given stream. One camera holds at most one stream.
   *
   * Built over EVERY floor — see `camerasAllFloors`. A tile whose stream is held by a camera on
   * another floor must say so rather than offering to link it a second time.
   *
   * ⚠️ **The index is FLOOR-LOCAL, and walking the floors is what makes it so.** An earlier
   * version indexed into the flattened `camerasAllFloors`, so `cameraDisplayName`'s "Camera N"
   * fallback counted across the whole plan: the same unnamed camera read "Camera 4" on its tile
   * and "Camera 1" on the plan, which indexes its own floor. The floor name is carried for the
   * same reason — the cross-floor refusal and the tile's placement footer name the floor that
   * holds the stream, which the active floor's list cannot see.
   */
  const cameraByEndpointId = useMemo(() => {
    const byId = new Map<
      number,
      {
        camera: FloorPlannerCamera;
        index: number;
        /** Which floor holds it — a tile's picture switches there before selecting it. */
        floorIndex: number;
        floorName: string | null;
        onActiveFloor: boolean;
      }
    >();
    (layout?.floors ?? []).forEach((floor, floorIndex) => {
      asArray(findLatestDesign(floor.designs)?.cameras)
        .filter(isLive)
        .forEach((camera, index) => {
          if (camera.rtspEndpointId != null)
            byId.set(camera.rtspEndpointId, {
              camera,
              index,
              floorIndex,
              floorName: floor.name ?? null,
              onActiveFloor: floorIndex === activeFloorIndex,
            });
        });
    });
    return byId;
  }, [layout, activeFloorIndex]);

  /*
   * Where each stream is, for the wall's placement tile and its counts (`camera-feeds-page-wall`).
   * Placement is WHOLE-TWIN: a stream held by a camera on any floor is placed, which is why this
   * reads `cameraByEndpointId` (every floor) rather than the active floor's cameras. The camera's
   * name is the one every other step-4 sentence uses — `cameraDisplayName` with the floor-local
   * index — and the tile shows it only when it differs from the stream's. A floor with no name is
   * called by its place, so the footer never reads as a dot and nothing.
   */
  const placementOf = useCallback(
    (feed: CameraWallFeed): CameraWallPlacement => {
      const held = cameraByEndpointId.get(Number(feed.id));
      if (!held) return { placed: false };
      return {
        placed: true,
        floor: floorLabel(held.floorName, held.floorIndex),
        camera: cameraDisplayName(held.camera, held.index),
      };
    },
    [cameraByEndpointId],
  );

  /*
   * Cameras on the plan that show nothing — the wall summary's lavender line. Every floor, like
   * placement: a camera upstairs with no stream still needs one. A camera linked to a stream id the
   * twin no longer has is not counted here (it has an id) and is not a feed either, so it is in
   * neither number — an accepted edge (spec R2).
   */
  const camerasWithoutStream = useMemo(
    () => camerasAllFloors.filter(c => c.rtspEndpointId == null).length,
    [camerasAllFloors],
  );

  const wallPlacement = useMemo<CameraWallPlacementProp>(
    () => ({
      of: placementOf,
      filter: placementFilter,
      onFilterChange: setPlacementFilter,
      camerasWithoutStream,
    }),
    [placementOf, placementFilter, camerasWithoutStream],
  );

  /*
   * Which tiles' PICTURES select a camera: a linked stream's does, named for the camera it selects
   * and the stream it shows. An unlinked stream's picture keeps expanding (Rob, 2026-09-30), so it
   * returns null. Stable by `useCallback`, for the tiles' memo.
   */
  /*
   * The selected tile says so in its NAME as well as its glow. The tile draws the glow and, with
   * `selectAction`, no pressed state, so the words are this consumer's to supply — as the observer
   * picker's *Drawing on this* caption does. Every visible corner of the tile is already spoken for,
   * so it lives in the accessible name (Copilot review, #1841). The plan's selection is read here
   * rather than below because this callback is declared first.
   */
  const selectedEntityIds = useEditorStore(s => s.selectedEntityIds);
  const feedSelect = useCallback(
    (feed: CameraWallFeed) => {
      const held = cameraByEndpointId.get(Number(feed.id));
      if (!held) return null;
      const name = cameraDisplayName(held.camera, held.index);
      const selected = selectedEntityIds.includes(held.camera.refid);
      return { label: `Select ${name}, which shows ${feed.name}${selected ? ' (selected)' : ''}` };
    },
    [cameraByEndpointId, selectedEntityIds],
  );

  /* ---------------- linking and placing ---------------- */

  /**
   * The stream's real name, or `null` when the read has not given it. The name rules compare a
   * camera's name with this, never with a fallback such as "Stream 12" (`nameAfterLink`).
   */
  const streamNameOrNull = useCallback(
    (id: number): string | null => {
      const s = streams?.find(e => e.id === id);
      return s ? streamDisplayName(s) : null;
    },
    [streams],
  );

  /** The stream's own label, reused as the name a newly placed camera starts with. */
  const streamLabel = useCallback(
    (id: number): string => streamNameOrNull(id) ?? `Stream ${id}`,
    [streamNameOrNull],
  );

  /*
   * The stream being dragged from the wall right now (`camera-feeds-page-plan-drop`). The plan needs
   * it to preview the drop, and cannot read it from the drag: `dataTransfer` is sealed during
   * `dragover`. The tile reports its own start and end (`CameraWallFeedDrag.onDragStart/End`).
   */
  const [draggingStreamId, setDraggingStreamId] = useState<number | null>(null);
  /*
   * ⚠️ **The backstop.** `dragend` fires on the node the drag STARTED on. A source tile that leaves
   * the DOM mid-drag never fires it, and the plan would go on previewing a drag that is over — so
   * while one is in flight the document's own `dragend` and `drop` end it too. Capture phase, so a
   * handler that stops propagation cannot hide either.
   */
  const dragging = draggingStreamId !== null;
  useEffect(() => {
    if (!dragging) return;
    const end = () => setDraggingStreamId(null);
    document.addEventListener('dragend', end, true);
    document.addEventListener('drop', end, true);
    return () => {
      document.removeEventListener('dragend', end, true);
      document.removeEventListener('drop', end, true);
    };
  }, [dragging]);
  const activeStreamDrag = useMemo<ActiveStreamDrag | null>(
    () =>
      draggingStreamId == null
        ? null
        : { rtspEndpointId: draggingStreamId, label: streamLabel(draggingStreamId) },
    [draggingStreamId, streamLabel],
  );

  /**
   * Write a camera into the plan, and — unless told otherwise — report it to the host, which asks
   * its draft saver for a save (`camera-feeds-page-draft`).
   *
   * Two callers pass `report: false`, on purpose:
   * - the name while it is typed — it reports once, when its field is left;
   * - `claimStream`'s release — it is written in the same turn as the claim that caused it, and
   *   that claim's report sends both in one save, so the server never holds the stream half moved.
   */
  const writeCamera = useCallback(
    (refid: string, updates: Partial<FloorPlannerCamera>, report = true) => {
      updateCamera(refid, updates);
      markEntityModified(refid);
      if (report) onCameraEdit?.();
    },
    [updateCamera, markEntityModified, onCameraEdit],
  );

  /**
   * Free a stream so `refid` may take it — or refuse, and say why.
   *
   * Returns `null` when the stream is now free for this camera, and a sentence when it is not.
   * The rule lives here because only the stage knows the whole plan, and the refusal turns on
   * **which floor the current holder is on.**
   *
   * ⚠️ **A cross-floor holder is refused, not unlinked, and that is a limit of the store rather
   * than a preference.** `updateCamera` writes through `updateDesign`, which resolves
   * `layout.floors[activeFloorIndex]` — so a write aimed at a camera on another floor matches no
   * refid and lands nowhere, silently. Unlinking a downstairs holder from up here would therefore
   * appear to work and leave BOTH cameras holding the stream, which is the exact defect this rule
   * exists to prevent, arrived at by trying to prevent it.
   *
   * ⚠️ **It is shared by every route a stream is claimed on, and that is the whole point** — the
   * panel's picker and Change, a confirmed Replace, and the drop. When this rule lived inside one
   * route's handler, another route wrote the stream onto a camera with no check at all — two
   * cameras holding one stream, persisted, with undo off. Found by review on an earlier PR.
   *
   * ## Split in two (`camera-feeds-page-plan-drop`)
   *
   * The READ-ONLY half of `claimStream`: what claiming `endpointId` for `refid` would do, worked out
   * and written nowhere (`camera-feeds-page-plan-drop`). The drag's hint asks it on every
   * `dragover`, so the hint says exactly what the drop will — a refusal before the drop, or the
   * camera the drop would take the stream from. `claimStream` below is this plus the release write;
   * there is no second copy of the rule.
   */
  const checkStreamClaim = useCallback(
    (
      refid: string | null,
      endpointId: number,
      opts: { replacing?: boolean } = {},
    ): StreamClaimAnswer => {
      /*
       * ⚠️ `refid` is `null` when a drop landed on empty plan and the camera it is asking about is
       * ABOUT TO BE MINTED. `null` rather than a sentinel string, so the case is in the type
       * rather than in a convention: both searches below then simply match nothing, which is the
       * right answer for a camera that holds nothing and is not yet on the plan.
       */
      const target = cameras.find(c => c.refid === refid);
      const previous = camerasAllFloors.find(
        c => c.refid !== refid && c.rtspEndpointId === endpointId,
      );

      /*
       * The cross-floor holder is checked FIRST, and wins over a replace: a stream held downstairs
       * cannot be released from up here, so a drop of it on a busy camera is refused with the
       * holder's floor rather than asked about (`camera-feeds-page-link`, spec R8).
       */
      if (previous && !cameras.some(c => c.refid === previous.refid)) {
        // The SAME name the tile uses. Read from `cameraByEndpointId`, whose index is floor-local:
        // taken from the flattened list instead, an unnamed holder was announced as "Camera 4" here
        // and called "Camera 1" everywhere else on the stage.
        const held = cameraByEndpointId.get(endpointId);
        const floorName = held?.floorName ?? floorNameHolding(layout, previous.refid);
        const name = held
          ? cameraDisplayName(held.camera, held.index)
          : cameraDisplayName(previous, 0);
        return {
          refusal:
            `${name} already shows ` +
            `this stream${floorName ? ` on ${floorName}` : ' on another floor'}. ` +
            'Unlink it there first.',
        };
      }
      const release = previous ?? null;

      /*
       * The camera already shows ANOTHER stream (`camera-feeds-page-link`, frame 08). Refused until
       * this feature; now the drop asks once and replaces. A caller that has already decided — the
       * picker's Change, or a confirmed Replace — passes `replacing` and goes straight through.
       */
      if (
        !opts.replacing &&
        target &&
        target.rtspEndpointId != null &&
        target.rtspEndpointId !== endpointId
      ) {
        return { replace: { from: target.rtspEndpointId, target }, release };
      }
      return { release };
    },
    [camerasAllFloors, cameras, cameraByEndpointId, layout],
  );

  /*
   * `checkStreamClaim` plus its one write, the release. Every caller has decided to link — the panel,
   * a confirmed Replace, a drop the check allowed — so a camera showing another stream is no
   * obstacle here (`replacing`).
   */
  const claimStream = useCallback(
    (refid: string | null, endpointId: number): string | null => {
      const check = checkStreamClaim(refid, endpointId, { replacing: true });
      if ('refusal' in check) return check.refusal;
      if (!check.release) return null;
      /*
       * ⚠️ **`report: false` — the release rides the caller's own save, and that is what makes the
       * hand-over atomic.**
       *
       * Persisting here fired a whole-layout PUT before the caller had written anything, so moving
       * a stream from one camera to another was TWO saves with a state nobody asked for in
       * between: the stream unlinked from its old camera and not yet on the new one. If the second
       * failed, that intermediate state is what the server kept. Worse, the first save's
       * `markClean()` landed after local writes it had never carried, so `isDirty` went false while
       * a camera was still unsaved — and the leave guard reads nothing but `isDirty`, so the
       * operator could close the tab with no warning.
       *
       * Every caller writes and reports immediately after a `null` return — `linkStream` and the
       * drop — so the release always lands, in one save with the write that caused it.
       */
      // The camera letting go is unlinked, so a name it took from this stream is cleared, exactly as
      // Unlink clears it (`nameAfterUnlink`; Copilot review) — the name follows the stream.
      writeCamera(
        check.release.refid,
        {
          rtspEndpointId: null,
          rtspEndpointBid: null,
          name: nameAfterUnlink({
            name: check.release.name ?? '',
            streamName: streamNameOrNull(endpointId),
          }),
        },
        false,
      );
      return null;
    },
    [checkStreamClaim, writeCamera, streamNameOrNull],
  );

  /**
   * Make a camera show a stream — every route that can REPLACE a stream comes here: the picker,
   * Change and a confirmed Replace (`camera-feeds-page-link`, spec R4 and its amendment 1). A drop
   * links through `onStreamLinked` instead — onto empty plan, which mints a camera, or onto a camera
   * with no stream — applying the same claim and the same `nameAfterLink`, because the planner
   * reports that edit itself.
   *
   * A camera already showing a stream is not an obstacle (`replacing`): the operator chose to
   * replace it, by picking or by confirming. A stream held by another camera on THIS floor lets go
   * in the same turn and rides this one save (`claimStream`); one held on another floor is refused,
   * as everywhere.
   *
   * The name follows the stream when the camera was never named or was named after the stream it
   * showed (`nameAfterLink`), and the camera, the name and the release land in ONE reported write.
   */
  const linkStream = useCallback(
    (refid: string, endpointId: number): string | null => {
      const refusal = claimStream(refid, endpointId);
      if (refusal) {
        setAnnouncement(refusal);
        return refusal;
      }
      // Read from the STORE: the release above has just written it (`readLiveCamera`).
      const { camera, index } = readLiveCamera(refid);
      if (!camera) return null;

      const oldId = camera.rtspEndpointId ?? null;
      const oldName = oldId != null ? streamNameOrNull(oldId) : null;
      const newName = streamLabel(endpointId);
      const before = cameraDisplayName(camera, index);
      const stream = streams?.find(e => e.id === endpointId);
      writeCamera(refid, {
        rtspEndpointId: endpointId,
        rtspEndpointBid: stream?.bid || null,
        name: nameAfterLink({
          name: camera.name ?? '',
          oldStreamName: oldName,
          newStreamName: newName,
        }),
      });
      useEditorStore.getState().selectEntity(refid);
      setAnnouncement(
        oldId != null && oldId !== endpointId
          ? `${newName} replaces ${oldName ?? streamLabel(oldId)} on this camera. ${
              oldName ?? streamLabel(oldId)
            } is back in Not placed.`
          : `${newName} linked to ${before}.`,
      );
      return null;
    },
    [claimStream, streams, streamLabel, streamNameOrNull, writeCamera],
  );

  /**
   * Take a camera's stream away (spec R6). The camera stays where it is with its cone; observers
   * stay with the stream, which they key on; a name taken from the stream is cleared, so the next
   * stream linked names the camera (ruling 3, maria@twinlabs.ai 2026-10-11). One reported write.
   */
  const unlinkStream = useCallback(
    (refid: string) => {
      const index = cameras.findIndex(c => c.refid === refid);
      const camera = index >= 0 ? cameras[index] : null;
      if (!camera || camera.rtspEndpointId == null) return;
      const oldId = camera.rtspEndpointId;
      const label = streamLabel(oldId);
      writeCamera(refid, {
        rtspEndpointId: null,
        rtspEndpointBid: null,
        name: nameAfterUnlink({ name: camera.name ?? '', streamName: streamNameOrNull(oldId) }),
      });
      setAnnouncement(
        `${cameraDisplayName(camera, index)} no longer shows ${label}. ${label} is back in Not placed.`,
      );
    },
    [cameras, streamLabel, streamNameOrNull, writeCamera],
  );

  /** Every stream a camera holds, on ANY floor: the Stream picker offers only the rest. */
  const heldStreamIds = useMemo(() => new Set(cameraByEndpointId.keys()), [cameraByEndpointId]);

  /**
   * The wall's own order for the Stream picker (`compareCameraWallFeeds`, under the wall's lifted
   * sort), so the picker and the wall's Not placed run list streams the same way. A stream the wall
   * has no feed for yet is compared by its name alone, as an undated feed.
   */
  const streamOrder = useMemo(() => {
    const compare = compareCameraWallFeeds(wallSort);
    const byId = new Map(feeds.map(f => [Number(f.id), f]));
    const feedOf = (s: RTSPEndpoint): CameraWallFeed =>
      byId.get(s.id) ?? { id: String(s.id), name: streamDisplayName(s), state: 'connecting' };
    return (a: RTSPEndpoint, b: RTSPEndpoint) => compare(feedOf(a), feedOf(b));
  }, [wallSort, feeds]);

  /*
   * What the polite live region is saying. Criterion 13: linking, placing and a refused link each
   * announce. Colour and a moved control announce nothing, so without this a screen-reader
   * operator performs the stage's central action and is told precisely nothing about whether it
   * worked — the change is silent on screen too, because the tile's label is the only thing that
   * moves and it is somewhere else on the page.
   */
  const [announcement, setAnnouncement] = useState('');
  /** The Replace being asked about, when a stream was dropped on a camera showing another. */
  const [pendingReplace, setPendingReplace] = useState<PendingReplace | null>(null);
  /** Alternates the selection announcements so a repeat still changes the live region. */
  const announceNonceRef = useRef(0);

  /** Stable, so the memoised tiles keep their bail-out — an inline arrow here defeats it for all. */
  /*
   * STABLE across layout writes, through a ref: `CameraWallTile`'s memo compares `onSelectFeed` by
   * identity, and `cameraByEndpointId` changes on every plan write — so a dependency on it
   * re-rendered all fourteen tiles on every keystroke in the camera panel's Name (review finding).
   */
  const cameraByEndpointIdRef = useRef(cameraByEndpointId);
  cameraByEndpointIdRef.current = cameraByEndpointId;
  /*
   * Select the camera that holds a stream — what a linked tile's picture does.
   * Switch to the holder's floor first: `setActiveFloor` clears the selection, so the select must
   * come after it. Selecting through the editor store is what the plan's own camera icon does, so
   * the plan highlights the camera and the camera panel opens on it with nothing else to wire.
   *
   * Focus stays on the tile, so a keyboard user can carry on along the list.
   *
   * Returns false for a stream no camera holds, so the caller decides what that means.
   */
  const selectHolder = useCallback((id: string | number): boolean => {
    const held = cameraByEndpointIdRef.current.get(Number(id));
    if (!held) return false;
    const switched = !held.onActiveFloor;
    if (switched) useFloorplanStore.getState().setActiveFloor(held.floorIndex);
    useEditorStore.getState().selectEntity(held.camera.refid);
    /*
     * And bring it into view — but only "if needed" (`camera-feeds-page-stage`, R13): a camera
     * already drawn well inside the clear area stays where the operator left the view, and one
     * behind the stream list counts as hidden (`plan-fly-to`, R3.4, R5.2). The fly-to glides a
     * frame after a floor switch's auto-fit, so a camera on the floor just switched to is usually
     * in view after the fit and does not move.
     */
    flyToOnPlan(held.camera.refid, { onlyIfHidden: true });
    const name = cameraDisplayName(held.camera, held.index);
    // The floor, when the pick changed it (R14): the plan moved under a screen-reader user. An
    // unnamed floor is called by its place, as the wall's placement footer calls it.
    const where = switched ? ` on ${floorLabel(held.floorName, held.floorIndex)}` : '';
    /*
     * A trailing no-break space on every other announcement. The live region only speaks when
     * its text CHANGES, so selecting Cardio, then Track on the plan (which announces nothing),
     * then Cardio again set the same string and a screen-reader user heard nothing (review
     * finding). The space is not read aloud.
     */
    announceNonceRef.current += 1;
    const tail = announceNonceRef.current % 2 === 0 ? '\u00a0' : '';
    setAnnouncement(`${name} selected${where}.${tail}`);
    return true;
  }, []);

  /*
   * A linked stream's PICTURE — it selects the camera, exactly as clicking the camera's icon on
   * the plan does (Rob, 2026-09-30). Stable through the same ref, for the same memo reason.
   */
  const handleSelectFeed = useCallback(
    (id: string | number) => {
      selectHolder(id);
    },
    [selectHolder],
  );

  /*
   * The tiles to draw selected: every stream held by a camera in the editor's selection, however it
   * got there — this stream's picture, its footer label, the camera's icon on the plan, a link or a
   * drop. Keyed by the wall's own `feed.id`, whatever type the feed hook produced.
   */
  const selectedFeedIds = useMemo(() => {
    const ids = new Set<CameraWallFeed['id']>();
    if (selectedEntityIds.length === 0) return ids;
    for (const feed of feeds) {
      const held = cameraByEndpointId.get(Number(feed.id));
      if (held && selectedEntityIds.includes(held.camera.refid)) ids.add(feed.id);
    }
    return ids;
  }, [feeds, selectedEntityIds, cameraByEndpointId]);

  /*
   * The plan header (`camera-feeds-page-stage`, R3–R7): each floor's camera count on its tag, and
   * the camera legend at the end of the floor strip. From `planLayout`, so another twin's layout
   * left in the store is never counted.
   */
  const floorCounts = useMemo(() => floorCameraCounts(planLayout), [planLayout]);

  /*
   * The empty-plan card (R8–R10), shown only when every one of these is known to be true: this
   * twin's plan, no camera on ANY floor (a floor that is empty while another has cameras shows its
   * count, not the card — the design draws the card for a twin with no cameras), and a streams read
   * that has come back with at least one stream. While the read loads, after it fails, with a
   * refused token, or with no streams, the wall says what is true, and the card does not promise a
   * stream to drag.
   */
  /*
   * `onboarding-persistent-scene-camera-feeds-glass`, R6.3 (D7): the card now reads the CURRENT
   * floor, with that floor's name under its title, so an empty floor upstairs shows it while the
   * ground floor has cameras. The streams conditions are kept, and it can be closed for this visit,
   * floor by floor (R6.5): `emptyPlanClosed` is component state, so the next visit shows it again.
   */
  const [emptyPlanClosed, setEmptyPlanClosed] = useState<ReadonlySet<number>>(() => new Set());
  const showEmptyPlan =
    hasPlan &&
    (floorCounts[activeFloorIndex] ?? 0) === 0 &&
    streamsRead?.status === 'ready' &&
    streamsRead.value.length > 0 &&
    !tokenRefused &&
    !emptyPlanClosed.has(activeFloorIndex);

  /*
   * Hover lights the cone (R11, R12): the wall reports the tile under the pointer or holding focus,
   * and the plan draws that stream's camera in card 24's lit look — only when the camera is on the
   * floor being shown, and never while a stream is being dragged (the drag owns the plan's looks).
   * Hovering never switches floor, pans or selects. Only a stream some camera holds is kept: a tile
   * that can light nothing (most of the wall, Not placed comes first) stores null, so React skips
   * re-rendering the step, the planner and the canvas as the pointer sweeps across those tiles.
   */
  const [hoveredFeedId, setHoveredFeedId] = useState<CameraWallFeed['id'] | null>(null);
  const handleFeedHover = useCallback(
    (id: CameraWallFeed['id'] | null) =>
      setHoveredFeedId(id != null && cameraByEndpointIdRef.current.has(Number(id)) ? id : null),
    [],
  );
  const streamDragActive = useEditorStore(s => s.streamDragActive);
  const litCameraRefid = useMemo(() => {
    if (hoveredFeedId == null || streamDragActive) return null;
    const held = cameraByEndpointId.get(Number(hoveredFeedId));
    return held?.onActiveFloor ? held.camera.refid : null;
  }, [hoveredFeedId, streamDragActive, cameraByEndpointId]);

  /* ---------------- the camera panel (`camera-feeds-page-panel`, `unified-camera-panel`) ---------------- */

  /*
   * The camera the panel shows, whether it is new, and where focus goes when it closes
   * (`useCameraPanelHost`, shared with Floor Plan).
   */
  const { panelRefid, panelRefidRef, panelIsNew, panelReturnTo } = useCameraPanelHost({
    cameras,
    activeFloorIndex,
    hasPlan,
    selectedEntityIds,
    planPaneRef,
  });
  /*
   * The glass layout (`onboarding-persistent-scene-camera-feeds-glass`, R2–R6). The RTSP streams
   * list is a list panel over the plan: open on every visit (never stored), folded and reopened by
   * the person, with a search that is never stored either. At 1280 px or narrower the wall is
   * 420 px with the xs chips, so every chip row stays on one line (R3.6).
   */
  const narrow = useMatchesMedia(NARROW_VIEWPORT_QUERY);
  const wallWidth = narrow ? WALL_WIDTH_NARROW : WALL_WIDTH;
  /*
   * The camera panel floats over the plan in the plan card (`unified-camera-panel`, R1). Its layer
   * starts beside the stream list, at the list's open width (nothing moves on fold, R5.1), less the
   * card's 8 px edge: the plan runs under the glass, so a card moved to the far left, or stored there
   * before the plan did, would otherwise sit under the step list (`camera-feeds-card-clear-of-list`).
   */
  const planCards = usePlanCardLayer(
    user?.sub,
    setAnnouncement,
    CARD_ROOM,
    // Never so far right that the 340 px panel and the card's edges no longer fit (a narrow window).
    `min(calc(${GLASS_START} + ${wallWidth} + ${GLASS_GAP} - ${GLASS_EDGE}), calc(100% - ${DOCKED_CARD_WIDTHS.inset} - 2 * ${space[3]}))`,
  );
  const [listOpen, setListOpen] = useState(true);
  const [query, setQuery] = useState('');
  /* The latest text wins (Failure States): the wall filters on the deferred value. */
  const wallQuery = useDeferredValue(query);
  const onListOpenChange = useCallback((open: boolean) => {
    setListOpen(open);
    setAnnouncement(open ? 'RTSP streams list opened' : 'RTSP streams list folded');
  }, []);

  /*
   * *{k} of {m} RTSP streams shown*, once typing settles; nothing for an empty search (R3.4). The
   * count is the wall's own shown set (`cameraWallShownFeeds`: the chips, then the search), so it is
   * the number of tiles on screen. Keyed on the typing alone: a frame landing or a camera placed
   * while a search stands says nothing, and never talks over the stage's own sentences.
   */
  const shownCount = useRef(() => ({ shown: 0, total: 0 }));
  shownCount.current = () => ({
    shown: cameraWallShownFeeds(feeds, {
      placementFilter,
      activeFilters: wallFilters,
      query,
      placementOf,
    }).length,
    total: feeds.length,
  });
  useEffect(() => {
    if (query.trim() === '') return undefined;
    const timer = window.setTimeout(() => {
      const { shown, total } = shownCount.current();
      setAnnouncement(`${shown} of ${total} RTSP streams shown`);
    }, SEARCH_ANNOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  /* The folded bar's summary: what is left to place, over every feed; empty while loading or failed (D4). */
  /* Still loading while the read has streams the wall has not been seeded with yet (the skeletons). */
  const streamsSeeding =
    streamsRead?.status === 'ready' && streamsRead.value.length > 0 && feeds.length === 0;
  const streamsSettled = streamsRead?.status === 'ready' && !tokenRefused && !streamsSeeding;
  const notPlaced = useMemo(
    () => feeds.filter(f => !placementOf(f).placed).length,
    [feeds, placementOf],
  );
  const foldedSummary = !streamsSettled
    ? undefined
    : notPlaced === 0
      ? 'All placed'
      : `${notPlaced} not placed`;

  /*
   * The camera card at its home pushes the rail and the floors left of it (R6.1–6.2); a card moved
   * away leaves both at the edge. Its width comes from the card's own measurement.
   */
  const [cardAtHome, setCardAtHome] = useState(true);
  const [cardWidth, setCardWidth] = useState(0);
  /*
   * The card reports its size and its place from inside its own ResizeObserver (`MovableCard`
   * measures in a flushSync, and a sync commit flushes its effects at once). Applied there, the rail
   * and the floor slot would resize inside that same delivery, and the browser reports a
   * ResizeObserver loop (the dev overlay showed it on the real app). So both land on the next
   * frame, after the delivery; a later report in the same frame replaces an earlier one.
   */
  const cardFrames = useRef(new Map<'size' | 'home', number>());
  const nextFrame = useCallback((key: 'size' | 'home', apply: () => void) => {
    const pending = cardFrames.current.get(key);
    if (pending !== undefined) window.cancelAnimationFrame(pending);
    cardFrames.current.set(
      key,
      window.requestAnimationFrame(() => {
        cardFrames.current.delete(key);
        apply();
      }),
    );
  }, []);
  useEffect(() => {
    const frames = cardFrames.current;
    return () => frames.forEach(id => window.cancelAnimationFrame(id));
  }, []);
  const onCardSize = useCallback(
    (size: { w: number; h: number }) => nextFrame('size', () => setCardWidth(size.w)),
    [nextFrame],
  );
  const onCardAtHomeChange = useCallback(
    (atHome: boolean) => nextFrame('home', () => setCardAtHome(atHome)),
    [nextFrame],
  );
  const cardBesideRail = panelRefid != null && cardAtHome && cardWidth > 0;
  // `plan-fly-to`, R3: what covers the plan, the camera card counting while it is open at home.
  usePlanCoverInsets(planPaneRef, () => panelRefid != null && cardAtHome);
  const besideCard = (gaps: number) =>
    besideCardRight({ open: panelRefid != null, atHome: cardAtHome, width: cardWidth }, gaps);
  /* Right of the wall, at its open width even while it is folded (R4.5, R5.1: nothing moves on fold). */
  const besideWall = `calc(${GLASS_START} + ${wallWidth} + ${GLASS_GAP})`;
  const floorSelectorSlot = useMemo(
    () => ({ left: besideWall, right: besideCard(2), top: PANEL_TOP }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [besideWall, cardBesideRail, cardWidth],
  );
  const controlsInset = useMemo(
    () => ({ top: PANEL_TOP, right: besideCard(1) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cardBesideRail, cardWidth],
  );
  const legendInset = useMemo(
    () => ({ left: besideWall, bottom: GLASS_EDGE, right: besideCard(1) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [besideWall, cardBesideRail, cardWidth],
  );

  /*
   * The panel's Calibrate (`observer-autogen-plan-projection`): the calibration screen, laid over the
   * step. It belongs to the camera it was opened for, so it is gone
   * the moment the panel closes or re-targets, and follows a refreshed frame by feed id. Opening one
   * closes the other. Focus goes back to whatever opened it (the panel's Calibrate) on close.
   */
  const [calibrating, setCalibrating] = useState<{
    feedId: CameraWallFeed['id'];
    refid: string;
  } | null>(null);
  const calibratingFeed =
    calibrating && calibrating.refid === panelRefid
      ? (feeds.find(f => f.id === calibrating.feedId) ?? null)
      : null;
  const calibratingCamera =
    calibratingFeed && calibrating
      ? (cameras.find(c => c.refid === calibrating.refid) ?? null)
      : null;
  useEffect(() => {
    if (calibrating && !calibratingFeed) setCalibrating(null);
  }, [calibrating, calibratingFeed]);
  const calibrateOpenerRef = useRef<HTMLElement | null>(null);
  const calibrateFeed = useCallback(
    (feed: CameraWallFeed) => {
      const active = document.activeElement;
      calibrateOpenerRef.current = active instanceof HTMLElement ? active : null;
      if (panelRefidRef.current) setCalibrating({ feedId: feed.id, refid: panelRefidRef.current });
    },
    [panelRefidRef],
  );
  /*
   * The panel's full-screen picture is the panel's own (`unified-camera-panel`, R5): opening it
   * closes the calibration screen, as Enlarge did.
   */
  const closeCalibrationForPicture = useCallback(() => setCalibrating(null), []);

  /** The panel's calibration row (`unified-camera-panel`, R3): only this step gives one. */
  const calibration = useMemo(() => ({ onCalibrate: calibrateFeed }), [calibrateFeed]);
  const closeCalibration = useCallback((outcome: 'saved' | 'cancelled') => {
    setCalibrating(null);
    if (outcome === 'saved') setAnnouncement('Calibration saved');
    // After the screen has gone, so the opener is reachable again.
    requestAnimationFrame(() => {
      const opener = calibrateOpenerRef.current;
      if (opener?.isConnected) opener.focus();
    });
  }, []);

  /*
   * Delete Camera (R10): through the planner's own flow, by request — the observer cascade, today's
   * warning when the camera has observers, the announcement, and `onCameraEdited` for the draft.
   */
  const requestCameraDelete = useCallback((refid: string) => {
    setCalibrating(null);
    useEditorStore.getState().requestCameraDelete(refid);
  }, []);

  /** What the panel's picture can say about the stream list: a refused session is a failed read. */
  const panelStreamsStatus: CameraPanelStreamsStatus = tokenRefused
    ? 'failed'
    : (streamsRead?.status ?? 'loading');

  /* ---------------- dragging a feed onto the plan ---------------- */

  /*
   * The tile's drag payload. A POINTER, not data: the stream's own id and nothing else.
   *
   * Every value that reaches plan JSON — the name, the BID — is resolved below from `streams`,
   * this page's own already-read list. So nothing a hostile page can write into a drag payload can
   * reach the plan, which is a stronger guarantee than sanitising fields one by one and is
   * available only because a feed is identified by a single number.
   *
   * STABLE by `useCallback`, like every per-feed function handed to the wall: the wall calls it per
   * tile per render and `CameraWallTile`'s comparator compares the result by value.
   */
  const feedDrag = useCallback((feed: CameraWallFeed): CameraWallFeedDrag | null => {
    /*
     * ⚠️ **No usable id means NOT DRAGGABLE, rather than draggable and inert.**
     *
     * `CameraWallFeed['id']` is `string | number`. `Number('abc')` is `NaN`, and `JSON.stringify`
     * writes that as `null` — a payload the reader refuses with nothing but a console line, so the
     * operator drags a tile that looks draggable and watches nothing happen. Returning `null` here
     * means the tile simply is not a drag source, which is honest, and the camera panel's stream
     * picker still reaches the same outcome. Not reachable today — `useCameraWallFeeds` normalises `id` off an
     * integer column — but the wall's published type does not promise that, and the repo's other
     * consumer of the same field already guards it (`ObserverStage`'s tile `pick`).
     */
    /*
     * ⚠️ `Number('')` and `Number('  ')` are **0**, not `NaN` — and 0 is a legitimate stream id, so
     * an integer check alone lets a blank id through as "stream 0". Reject an empty string before
     * coercing. (`useCameraWallFeeds` maps a blank to `NaN` today, so this is not reachable from
     * the wizard's own feeds — but the wall's published `id` type is `string | number`, and the
     * contract this guard states is "no usable id means not draggable".)
     */
    if (typeof feed.id === 'string' && feed.id.trim() === '') return null;
    const rtspEndpointId = Number(feed.id);
    if (!Number.isInteger(rtspEndpointId)) return null;
    return {
      payload: JSON.stringify({ rtspEndpointId }),
      label: feed.name,
      // Built from the id alone — the tile's memo compares a drag by its payload, so two drags equal
      // by value must behave identically (`CameraWallFeedDrag`). The setter is stable.
      onDragStart: () => setDraggingStreamId(rtspEndpointId),
      onDragEnd: () => setDraggingStreamId(null),
    };
  }, []);

  /**
   * The drop's whole rule, read-only: a stream id this twin does not hold is refused, then
   * `checkStreamClaim`. ONE function, so the drop (`onStreamClaim`) and the drag's hint
   * (`onStreamClaimCheck`) cannot refuse differently (`camera-feeds-page-plan-drop`, spec R1.4).
   */
  const checkStreamDrop = useCallback(
    (endpointId: number, targetRefid: string | null) =>
      streams?.some(e => e.id === endpointId)
        ? checkStreamClaim(targetRefid, endpointId)
        : { refusal: UNKNOWN_STREAM },
    [streams, checkStreamClaim],
  );

  /**
   * Decide whether a dropped feed may be linked here — BEFORE the canvas writes anything.
   *
   * `targetRefid` is `null` when the drop landed on empty plan and a camera is about to be minted.
   * `claimStream` reads that as "a camera holding nothing", which is exactly what it will be.
   */
  const onStreamClaim = useCallback(
    (endpointId: number, targetRefid: string | null): StreamClaimResult => {
      // Every drop puts the camera tool away — refused and deferred ones too, since neither reaches
      // `onStreamLinked`: a drag that began with the tool armed must not leave the next click
      // minting a camera (review; Copilot, PR #2004).
      useEditorStore.getState().setToolMode('select');
      /*
       * An id this twin does not hold. Only reachable from a payload no tile in this product
       * writes — but the whole point of carrying a pointer rather than data is that the pointer is
       * resolved against our own list rather than trusted, and an unresolvable one is refused.
       */
      const check = checkStreamDrop(endpointId, targetRefid);
      if ('refusal' in check) return check.refusal;
      /*
       * The camera shows another stream: ask first (frame 08). The canvas writes nothing; the
       * confirm below decides, and Replace goes through `linkStream` like every other link.
       */
      if ('replace' in check) {
        const { from, target: camera } = check.replace;
        const index = cameras.indexOf(camera);
        const oldName = streamLabel(from);
        const newName = streamLabel(endpointId);
        const name = camera.name ?? '';
        const renamed = nameAfterLink({
          name,
          oldStreamName: streamNameOrNull(from),
          newStreamName: newName,
        });
        const { title, body } = replaceSentences({
          oldName,
          newName,
          renames: renamed === name ? false : name.trim() === '' ? 'unnamed' : 'after-old',
          holderName: check.release
            ? cameraDisplayName(check.release, cameras.indexOf(check.release))
            : null,
        });
        const floor = floorLabel(layout?.floors[activeFloorIndex]?.name, activeFloorIndex);
        setPendingReplace({
          refid: camera.refid,
          endpointId,
          from,
          releaseRefid: check.release?.refid ?? null,
          title,
          body,
          row: `${cameraDisplayName(camera, index)} · ${floor} · ${coverageReadout(
            effectiveCoverage(camera).fovAngle,
          )}`,
          oldName,
        });
        return { deferred: true };
      }
      return claimStream(targetRefid, endpointId);
    },
    [
      checkStreamDrop,
      claimStream,
      cameras,
      streamLabel,
      streamNameOrNull,
      layout,
      activeFloorIndex,
    ],
  );

  /**
   * `onStreamClaim` without the write, for the drag's hint (`camera-feeds-page-plan-drop`): the same
   * unknown-id refusal and the same `checkStreamClaim`, so what the hint says before the drop is
   * what the drop then does. The camera it would take the stream from is named as the hint names
   * it — `cameraDisplayName`, as every sentence on this stage does.
   */
  const onStreamClaimCheck = useCallback(
    (endpointId: number, targetRefid: string | null): StreamClaimCheck => {
      const check = checkStreamDrop(endpointId, targetRefid);
      if ('refusal' in check) return check;
      const releaseFrom = check.release
        ? cameraDisplayName(check.release, cameras.indexOf(check.release))
        : null;
      if ('replace' in check) return { replaceFrom: streamLabel(check.replace.from), releaseFrom };
      return { releaseFrom };
    },
    [checkStreamDrop, cameras, streamLabel],
  );

  /**
   * Write the link for a dropped feed, and say what happened.
   *
   * A drop puts the camera tool away: a drag that began with the tool armed must not leave the
   * operator's next click minting a second camera.
   *
   * ⚠️ **The camera is read from the STORE, not from `cameras`.** A camera minted microseconds ago
   * by `@repo/ui` is not in this closure's memoised list, so its display name and floor-local index
   * have to come from the live layout. Read BEFORE the name is written, so a placement announces
   * "Camera 3 placed, showing Free weights".
   */
  const onStreamLinked = useCallback(
    (endpointId: number, refid: string, outcome: 'placed' | 'linked') => {
      useEditorStore.getState().setToolMode('select');

      const { camera, index, activeFloorIndex: liveFloor } = readLiveCamera(refid);
      /*
       * ⚠️ **Not finding it is not a naming problem.** The camera was added to this store one tick
       * ago, so its absence means the write landed nowhere — `updateDesign` returns early when the
       * active floor has no design, silently. This is the one place that is detectable, so it must
       * neither pass in silence nor be announced as a success: the operator is told nothing was
       * placed, which is true, and the write below is not attempted because it would land nowhere
       * either.
       */
      if (!camera) {
        console.error(
          '[OnboardingCameras] a camera minted by a feed drop is absent from the live layout; ' +
            'the write was discarded. refid:',
          refid,
          'activeFloorIndex:',
          liveFloor,
        );
        setAnnouncement(
          `${streamLabel(endpointId)} could not be placed. Nothing was added to the plan.`,
        );
        return;
      }
      const cameraName = cameraDisplayName(camera, index);

      const stream = streams?.find(e => e.id === endpointId);
      writeCamera(refid, {
        rtspEndpointId: endpointId,
        rtspEndpointBid: stream?.bid || null,
        // A camera the drop MINTED starts named after the stream. One already on the plan follows
        // the stream only if it was never named (`nameAfterLink`, spec R5): a drop links exactly
        // as the picker does (frame 07, note 4), and a name the operator typed is kept.
        name:
          outcome === 'placed'
            ? streamLabel(endpointId)
            : nameAfterLink({
                name: camera.name ?? '',
                oldStreamName: null,
                newStreamName: streamLabel(endpointId),
              }),
      });

      setAnnouncement(
        outcome === 'placed'
          ? `${cameraName} placed, showing ${streamLabel(endpointId)}.`
          : `${streamLabel(endpointId)} linked to ${cameraName}.`,
      );
    },
    [streams, streamLabel, writeCamera],
  );

  /**
   * Replace (frame 08): link the dropped stream through the one link path — but only if the plan
   * still says what the confirm said. The camera may have been deleted, re-linked or the stream
   * placed elsewhere while the modal was open; writing then would replace something the operator
   * never saw, so nothing is written and they are told (spec R7).
   */
  const confirmReplace = useCallback(() => {
    const ask = pendingReplace;
    setPendingReplace(null);
    if (!ask) return;
    focusCameraOrPlan(ask.refid);
    // The same question again, against the plan as it is now: still a replace of the stream the
    // confirm named, releasing the camera it named (or none). Anything else writes nothing.
    const check = checkStreamDrop(ask.endpointId, ask.refid);
    if (
      !('replace' in check) ||
      check.replace.from !== ask.from ||
      (check.release?.refid ?? null) !== ask.releaseRefid
    ) {
      setAnnouncement('Nothing was replaced: the plan changed.');
      return;
    }
    linkStream(ask.refid, ask.endpointId);
  }, [pendingReplace, checkStreamDrop, linkStream]);

  const keepStream = useCallback(() => {
    if (pendingReplace) focusCameraOrPlan(pendingReplace.refid);
    setPendingReplace(null);
  }, [pendingReplace]);

  // No floor plan yet: cameras are placed with the Floor Plan section's camera
  // tool, so there's nothing to configure here until a plan exists.
  if (!hasPlan) {
    return (
      <div className={className}>
        <div className="feed-status">
          <Map24Regular className="status-icon" />
          <h3>No floor plan yet</h3>
          <p>
            Set up your floor plan first — cameras are added there with the camera tool, then
            configured here.
          </p>
        </div>
      </div>
    );
  }

  /*
   * The notice, and the reason it is a notice.
   *
   * NOTHING here gates the stage. Whatever the check found — or failed to find out — the sidebar,
   * the canvas and the camera panel render below. The four rules this wording follows:
   *
   *  1. Nothing claims a device is offline unless that has been established. "We could not check"
   *     and "there are no detections" are different sentences, and neither is "your box is down".
   *  2. The empty cases are two, not one: a twin with no edge device has nothing to be offline.
   *  3. The window is named, so the operator knows what was actually searched.
   *  4. Every notice says the work below still works, because it does.
   *
   * `Banner` (design card 09, "Inline banner"), `surface="on-solid"` — the wizard step is a white
   * panel — and no `icon`, so it renders no disc. Both match the Equipment step's uploader banners
   * (`AssetCsvUploader.tsx`) in this same wizard. Tone `warning` rather than `error`: `Banner` defaults its live region to `alert` for
   * `error` alone, and this must not interrupt work it is not blocking.
   */
  const notice = (() => {
    switch (check.kind) {
      case 'detections':
        // Silence is the happy path. A banner saying "all good" is noise on every load.
        return null;
      case 'checking':
        /*
         * `retry: true` WITH `busy`, not `retry: false`. Unmounting the button mid-check threw a
         * keyboard user's focus to <body> on every press — they then had to tab back through the
         * whole camera sidebar to reach it again, and if the check succeeded the button never came
         * back at all. Keeping it mounted and busy is what makes criterion 9's keyboard path
         * survive activation. It is safe to leave pressable-looking because `Button`'s `loading`
         * blocks activation and the ticket guard drops any superseded answer.
         */
        return {
          tone: 'info' as const,
          body: 'Checking for camera activity…',
          retry: true,
          busy: true,
        };
      case 'not-checked':
        return {
          tone: 'info' as const,
          body: 'Camera activity wasn’t checked because no twin is selected.',
          retry: false,
        };
      case 'no-devices':
        return {
          tone: 'info' as const,
          body:
            'No edge devices are registered for this twin. Feed setup below still works — nothing ' +
            'will produce detections until a device is claimed.',
          retry: true,
        };
      case 'no-detections':
        return {
          tone: 'info' as const,
          body:
            'No detections from this twin’s edge devices in the last 24 hours. That can mean the ' +
            'cameras aren’t streaming yet. Feed setup below still works.',
          retry: true,
        };
      case 'detections-partial':
        return {
          tone: 'warning' as const,
          body:
            'Detections found, but not every one of this twin’s ' +
            (check.deviceCount === null ? '' : `${check.deviceCount} `) +
            'edge devices could be checked.',
          retry: true,
        };
      case 'no-session':
        return {
          tone: 'warning' as const,
          body:
            'Camera activity couldn’t be checked — your session could not be confirmed. Reload the ' +
            'page to sign in again. Feed setup below still works.',
          // NO RETRY. There is no token to retry with, and the token is not re-requested.
          retry: false,
        };
      case 'unavailable':
        return {
          tone: 'warning' as const,
          body: `${check.message} This doesn’t mean your cameras are offline — feed setup below still works.`,
          retry: true,
        };
      default:
        /*
         * Exhaustiveness, checked by the compiler. The switch is the only thing standing behind the
         * docblock's promise that no state can be reached without a sentence written for it, and
         * without this a seventh member added later would fall through, return `undefined`, and
         * silently render no banner at all.
         */
        return assertNever(check);
    }
  })();

  /*
   * The activity notice, in the RTSP streams panel's header (R3.5), last under the wall's filters.
   * On glass, so it reads over the panel's wash. The `check-announcement` region stays in the
   * canvas area for the one state this banner does not render.
   */
  const noticeBanner = notice ? (
    <div className="check-notice" data-testid="check-notice">
      <Banner surface="on-glass" tone={notice.tone}>
        {notice.body}
        {notice.retry && (
          <div className="retry-slot">
            <Button
              variant="ghost"
              size="xs"
              loading={notice.busy ?? false}
              onClick={() => void runCheck()}
            >
              Check again
            </Button>
          </div>
        )}
      </Banner>
    </div>
  ) : null;

  /*
   * The RTSP streams list panel (R2), holding whichever of the four renderings is true. The wall
   * hands its header and body over through `frame`; the other three put theirs in the body.
   */
  const streamsPanel = (body: React.ReactNode, wallHeader?: React.ReactNode) => (
    <ListPanel
      name="RTSP streams"
      summary={foldedSummary}
      width={wallWidth}
      open={listOpen}
      onOpenChange={onListOpenChange}
      /* Off while a screen covers the step: the calibration screen, or the Replace question. */
      shortcutEnabled={!calibratingFeed && !pendingReplace}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: 'Search RTSP streams and cameras…',
        label: 'Search RTSP streams and cameras',
      }}
      header={
        wallHeader || noticeBanner ? (
          <>
            {wallHeader}
            {noticeBanner}
          </>
        ) : undefined
      }
    >
      {body}
    </ListPanel>
  );

  /*
   * The body in place of the wall's own while there is no wall to show (`CameraWall`'s
   * `bodyOverride`). Three renderings, and they deliberately share no sentence. The failed one
   * shows NO WALL: `CameraWall` says "This twin has no RTSP streams." for an empty feed list, and
   * printing that after a read that failed is the affirmative false claim this stage exists to remove.
   */
  const streamsBodyOverride: React.ReactNode | undefined =
    streamsRead == null ||
    streamsRead.status === 'loading' ||
    /*
     * ⚠️ The wall is not mounted until it actually HOLDS the injected streams.
     *
     * `useCameraWallFeeds` seeds from the injected list in an effect, so on the render that
     * first mounts `CameraWall` its feed list is still empty — and an empty feed list is what
     * makes the wall print "This twin has no RTSP streams.", inside an `EmptyInline` that
     * carries role="status" and is therefore ANNOUNCED. One frame is enough to make the same
     * affirmative false claim the failed branch below exists to prevent, arriving through the
     * loading door instead. A twin that genuinely has no streams still reaches the wall and
     * gets that sentence honestly, because the condition is on the read's own count.
     */
    (streamsRead.status === 'ready' &&
      streamsRead.value.length > 0 &&
      feeds.length === 0 &&
      !tokenRefused) ? (
      <div className="stream-loading" data-testid="streams-loading">
        <Skeleton shape="media" />
        <Skeleton shape="media" />
        <p>Loading this twin’s camera streams…</p>
      </div>
    ) : tokenRefused ? (
      /*
       * The wall needs a token of its own for the frames, and this one was refused — blocked
       * third-party cookies, a lapsed refresh token, an Auth0 blip. Its own sentence, because
       * the other three would all be false: the read did not fail, the twin is not empty, and
       * nothing is still loading. Retrying the stream read would not help, so it is not offered.
       *
       * It does NOT repeat "reload the page to sign in again" — the no-session notice above it
       * says that, from the same cause, and two banners giving one instruction is the
       * duplication this stage's renderings are kept distinct to avoid.
       */
      <div className="stream-failed" data-testid="streams-no-session">
        <Banner surface="on-solid" tone="error">
          This twin’s camera pictures could not be loaded, because your session could not be
          renewed. The notice above says what to do about it.
        </Banner>
      </div>
    ) : streamsRead.status === 'failed' ? (
      <div className="stream-failed" data-testid="streams-failed">
        <Banner surface="on-solid" tone="error">
          {streamsRead.reason}
          <div className="retry-slot">
            <Button
              variant="ghost"
              size="xs"
              aria-label="Retry reading this twin’s camera streams"
              onClick={() => liveConfiguration?.retry('streams')}
            >
              Retry
            </Button>
          </div>
        </Banner>
      </div>
    ) : undefined;

  /*
   * What to do on a floor with no camera yet (canvas frame 10; R6.3–6.5): a glass card centred on
   * the screen (the planner's stage is the whole window here), no scrim. Its layer passes every
   * pointer through, so a drop or a camera-tool click beside the card lands on the canvas; only the
   * card takes pointers. The planner renders it straight after the floor selector
   * (`afterFloorSelector`), so its Close tabs after the floors (spec Block A.1). No live region: it
   * is a load state, and placing the first camera is announced by the stage.
   */
  const emptyPlanCard =
    showEmptyPlan && layout ? (
      <div className="empty-plan-layer">
        <DockedCardFloating grip={null}>
          <DockedCard
            className="empty-plan"
            data-testid="empty-plan"
            placement="narrow"
            tone="cyan"
            icon={<Icon name="video" />}
            title="No cameras on the plan yet"
            headingLevel={4}
            sub={floorLabel(layout.floors[activeFloorIndex]?.name, activeFloorIndex)}
            onClose={() => setEmptyPlanClosed(prev => new Set(prev).add(activeFloorIndex))}
            closeLabel="Close"
          >
            <p className="empty-plan-lead">Drag an RTSP stream to where its camera hangs.</p>
            <p>
              The camera takes the RTSP stream’s name. To place a camera first and link it later,
              use the camera tool.
            </p>
          </DockedCard>
        </DockedCardFloating>
      </div>
    ) : null;

  return (
    <div className={className}>
      {/*
        The twin's streams, where the plan's cameras used to be: one panel at one place in the tree.
        The wall renders it in every state, taking `streamsBodyOverride` while there is no wall to
        show, so a search the person is typing in keeps focus when the streams arrive.
      */}
      {/*
        `data-planner-shortcuts="off"`: the planner listens for Delete, Backspace, R and C on the
        WINDOW, and a focused button here is none of the things it skips. A linked stream's picture
        selects its camera and leaves focus on that tile, so without this a keyboard operator who
        pressed Backspace next removed the camera — on a stage with undo off (review finding). The
        same marker the camera panel carries for the same reason.
      */}
      {/* Over the plan: a fly-to lands clear of it, and a camera behind it counts as hidden
        (`plan-fly-to`, R3.4). */}
      <div className="stream-panel" data-planner-shortcuts="off" data-plan-cover="left">
        <CameraWall
          layout="list-panel"
          frame={({ header, body }) => streamsPanel(body, header)}
          bodyOverride={streamsBodyOverride}
          chipSize={narrow ? 'xs' : 'dense'}
          query={wallQuery}
          feeds={feeds}
          tier="panel"
          sort={wallSort}
          activeFilters={wallFilters}
          onSortChange={setWallSort}
          onFiltersChange={setWallFilters}
          onRefreshAll={refreshAll}
          onRefreshFeed={refreshFeed}
          feedDrag={feedDrag}
          feedSelect={feedSelect}
          onSelectFeed={handleSelectFeed}
          selectedFeedIds={selectedFeedIds}
          zoomRegionsFor={zoomRegionsFor}
          isRefreshing={isRefreshing}
          placement={wallPlacement}
          onFeedHover={handleFeedHover}
          height="fill"
        />
      </div>

      <div className="canvas-area">
        {/*
          A PERMANENTLY-MOUNTED live region for the one state the banner does not render.
          `Banner` is its own `role="status"`, so while a notice is up it does the announcing —
          but the happy path renders no banner, which means the region UNMOUNTS rather than
          changing, and a screen-reader user who pressed Check again and succeeded was told
          nothing at all. That is the one state change a user deliberately triggered and waited
          on. This region carries text only when the banner is absent, so exactly one region
          announces per transition and they cannot double up.
        */}
        <div className="sr-only" role="status" data-testid="check-announcement">
          {check.kind === 'detections' ? 'Camera activity confirmed.' : ''}
        </div>

        {/*
          The stage's own polite region, PERMANENTLY MOUNTED and carrying an empty string when
          there is nothing to say — the same discipline as the region above it, and for the same
          reason: a region that unmounts between announcements does not announce, it disappears,
          and the insertion of a fresh one is not reliably read. Separate from that region rather
          than shared, so a detection result and a link confirmation cannot overwrite each other.
        */}
        <div className="sr-only" role="status" data-testid="stage-announcement">
          {announcement}
        </div>

        {/*
          What no camera sees, in words (`onboarding-status-colours-unseen`, R7): the plan's problem
          marks are colour on a canvas, so the counts are said here. Its own region, permanently
          mounted like the two above, so a camera write's confirmation and a count change cannot
          overwrite each other.
        */}
        <div className="sr-only" role="status" data-testid="unseen-announcement">
          {unseenAnnouncement}
        </div>

        {/*
          The plan, with the camera panel floating over it in the plan card (`unified-camera-panel`,
          R1, Rob 2026-10-29): the plan keeps its width when a camera is selected.
        */}
        <div className="planner-slot">
          <div className="plan-pane" ref={planPaneRef}>
            {/* The built-in properties editor is off; the camera panel presents the selected camera
              instead. */}
            {/*
            `cameras`, not `select`. The stage places, moves, rotates and deletes cameras — and
            nothing else on the plan may change while it is open (epic ledger row 23).
            `select` withheld the TOOLS and left the entities mutable, so equipment could still be
            dragged and Delete still reached the canvas.
          */}
            <TwinPlanner
              showToolbar={false}
              showFullscreenButton={false}
              showPropertiesPanel={false}
              toolset="cameras"
              onStreamClaim={onStreamClaim}
              onStreamLinked={onStreamLinked}
              activeStreamDrag={activeStreamDrag}
              onStreamClaimCheck={onStreamClaimCheck}
              onObserversRemoved={onObserversRemoved}
              // The plan's own camera gestures — move, turn, the camera tool, delete — saved like the
              // stage's writes (`camera-feeds-page-draft`, FR2).
              onCameraEdited={onCameraEdit}
              // The plan header and the hover light (`camera-feeds-page-stage`).
              floorCounts={floorCounts}
              // The glass furniture (`onboarding-persistent-scene-camera-feeds-glass`, R4–R6): the
              // floors float at the top of the free area, the legend bar (with Show map) at its
              // bottom left, and the rail sits beside the camera card at home.
              floorSelector="floating"
              floorSelectorSlot={floorSelectorSlot}
              afterFloorSelector={emptyPlanCard}
              canvasLegend={hasUnseen ? CAMERA_LEGEND_WITH_UNSEEN : CAMERA_LEGEND}
              legendLook="floating"
              legendInset={legendInset}
              showMapSwitch
              controlsInset={controlsInset}
              // Spaces and machines no camera sees, in the problem look (`onboarding-status-colours-unseen`).
              entityMarks={unseenMarks}
              litCameraRefid={litCameraRefid}
              unlinkedCamerasAreWork
              // The camera panel, floating over the plan (`unified-camera-panel`, R1): the plan
              // keeps its width when a camera is selected.
              canvasOverlay={planCards.render(
                <PlanCard
                  frame={planCards.frame}
                  name="camera panel"
                  homeInset={CARD_HOME_INSET}
                  onAtHomeChange={onCardAtHomeChange}
                  onSize={onCardSize}
                >
                  <CameraPanel
                    feeds={feeds}
                    streamsStatus={panelStreamsStatus}
                    streams={streams}
                    isNew={panelIsNew}
                    onWrite={writeCamera}
                    onAnnounce={setAnnouncement}
                    heldStreamIds={heldStreamIds}
                    streamOrder={streamOrder}
                    onLink={linkStream}
                    onUnlink={unlinkStream}
                    onPictureOpen={closeCalibrationForPicture}
                    onRequestDelete={requestCameraDelete}
                    calibration={calibration}
                    returnFocusTo={panelReturnTo}
                  />
                </PlanCard>,
              )}
            />
          </div>
        </div>
      </div>

      {/*
        The calibration screen, over the whole step rather than the plan pane alone: it is two panes
        side by side, and beside the 600 px stream rail and the 340 px panel the plan pane is too
        narrow for them at the rig's 1440 px.
      */}
      {calibratingFeed && calibratingCamera && layout ? (
        <div className="calibration-slot">
          <CameraCalibration
            camera={calibratingCamera}
            cameraName={cameraDisplayName(
              calibratingCamera,
              cameras.findIndex(c => c.refid === calibratingCamera.refid),
            )}
            layout={layout}
            floorIndex={activeFloorIndex}
            floorName={floorLabel(layout.floors[activeFloorIndex]?.name, activeFloorIndex)}
            feed={calibratingFeed}
            onWrite={writeCamera}
            onClose={closeCalibration}
            onRetryPicture={refreshFeed}
          />
        </div>
      ) : null}

      {pendingReplace ? (
        <LinkReplaceModal
          title={pendingReplace.title}
          body={pendingReplace.body}
          row={pendingReplace.row}
          oldName={pendingReplace.oldName}
          onReplace={confirmReplace}
          onKeep={keepStream}
        />
      ) : null}
    </div>
  );
};

export default styled(OnboardingCameras)`
  position: relative;
  height: 100%;
  width: 100%;

  /*
   * The calibration screen covers the step area, below any modal (those portal to the page). The
   * step now spans the window under the glass, so the slot is inset to where the step area was:
   * below the nav bar, and from the step list's right edge, which stays usable beside it (D2).
   */
  .calibration-slot {
    position: absolute;
    top: ${BAR_BOTTOM};
    left: calc(${GLASS_START} - ${GLASS_GAP});
    right: 0;
    bottom: 0;
    z-index: ${zIndex.overlay};
  }

  /*
   * The RTSP streams list panel's place (R2): beside the step list, below the bar, at the 8 px
   * edge. Above the plan (the canvas area is the layer under it). The slot itself passes pointers
   * through, so the plan beside a folded bar still takes them; the panel takes its own back.
   */
  .stream-panel {
    position: absolute;
    top: ${PANEL_TOP};
    left: ${GLASS_START};
    bottom: ${GLASS_EDGE};
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    pointer-events: none;
  }

  .stream-loading {
    display: flex;
    flex-direction: column;
    gap: ${space[3]};

    /* Tokens, because this block is NEWLY AUTHORED. R5 carves out MIGRATING this file's existing
       hexes; it does not license adding more. Same treatment as the sibling dialog in this PR. */
    p {
      margin: 0;
      font-size: ${typeRole.body2.fontSize};
      line-height: ${typeRole.body2.lineHeight};
      color: ${semantic.text.body};
    }
  }

  .stream-failed .retry-slot {
    margin-top: ${space[2]};
  }

  /* The plan, the whole window under the glass (R1). */
  .canvas-area {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
  }

  /* The activity notice, in the list panel's header (R3.5). */
  .check-notice .retry-slot {
    margin-top: ${space[2]};
  }

  /* min-height:0 is load-bearing: without it a flex item refuses to shrink below its content and
     the planner overflows the column instead of fitting inside it. */
  .planner-slot {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
  }

  .plan-pane {
    position: relative;
    flex: 1;
    min-width: 0;
    min-height: 0;
  }

  /* The planner keeps its layers to itself — the zoom buttons on the sticky layer, the selected
     camera's name on the tooltip layer — so nothing it draws rises over the step's own layers. */
  .plan-pane > :first-child {
    isolation: isolate;
  }

  /* Visually hidden, still announced. The standard clip-rect recipe rather than display:none or
     visibility:hidden, both of which remove the element from the accessibility tree and would make
     the live region silent — which is the defect it exists to fix. */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* NOTE: no backticks anywhere below — this is inside a template literal and one closes it.
     That has bitten twice in this file. */
  /*
   * The nothing-placed card's layer, centred on the screen with no scrim (R6.4). pointer-events none:
   * a drop or a camera-tool click on the plan beside the card reaches the canvas. The layer the
   * plan's old hint pill sat on: above the canvas, below the camera card.
   */
  .empty-plan-layer {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    z-index: ${zIndex.dropdown};
    pointer-events: none;
  }

  .empty-plan-layer .empty-plan {
    pointer-events: auto;
  }

  .empty-plan p {
    margin: 0;
  }

  .empty-plan .empty-plan-lead {
    font-weight: ${weight.semibold};
  }

  /* Retained for the "No floor plan yet" panel, which is a REAL precondition: this stage annotates
     a floor plan, so there is nothing to do here without one. Replacing that panel with a stream
     list belongs to the stream-first feature, not this one. */
  .feed-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    flex: 1;
    padding: 2rem;
    text-align: center;

    .status-icon {
      width: 40px;
      height: 40px;
      color: #a3a3a3;
    }

    h3 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1a1a1a;
    }

    p {
      margin: 0;
      max-width: 420px;
      font-size: 0.9rem;
      color: #707070;
    }
  }
`;
